import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { diagnosticsAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Temporary diagnostic panel: compares the Bookings vs Invoicing chart
 * aggregation against the drill-down aggregation for a given month,
 * showing the set-difference of bros_ids and totals.
 */
export function BillingVsSalesDivergencePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.billingVsSalesDivergenceCheck({
        month: '2026-05',
        currency: 'gbp',
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const dc = result?.display_currency || 'GBP';
  const patternBadge = result?.pattern === 'A'
    ? <Badge variant="outline" className="text-green-600 border-green-300">A (no divergence)</Badge>
    : result?.pattern === 'B'
      ? <Badge variant="destructive">B (forecast overlay)</Badge>
      : result?.pattern === 'C'
        ? <Badge variant="secondary">C (other)</Badge>
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Billing vs Sales: Chart/Drill-down Divergence Check
          <Badge variant="outline" className="text-xs font-normal">temporary</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Runs both the chart aggregation (actuals + forecast overlay) and the
          drill-down aggregation for May 2026 Bookings, then compares the
          resulting sets of bros_ids. Hardcoded to May 2026 for this investigation.
        </p>

        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run
        </Button>

        {result && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium">Classification</p>
                <div className="flex items-center gap-2">
                  {result.pattern === 'A'
                    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                    : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                  {patternBadge}
                </div>
                <p className="text-xs text-muted-foreground">{result.pattern_label}</p>
                {result.is_current_month && (
                  <p className="text-xs text-muted-foreground italic">
                    {result.month} is the current month (forecast overlap possible).
                  </p>
                )}
              </div>

              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Totals</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">Chart bar total</span>
                  <span className="tabular-nums font-mono">{formatCurrency(result.chart_total, dc)}</span>
                  <span className="text-muted-foreground">Drill-down total</span>
                  <span className="tabular-nums font-mono">{formatCurrency(result.drill_total, dc)}</span>
                  <span className="text-muted-foreground font-medium">Divergence</span>
                  <span className="tabular-nums font-mono font-medium">{formatCurrency(result.divergence, dc)}</span>
                </div>
              </div>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="border rounded p-2">
                <p className="text-lg font-mono tabular-nums">{result.chart_actuals_count}</p>
                <p className="text-xs text-muted-foreground">Chart actuals</p>
              </div>
              <div className="border rounded p-2">
                <p className="text-lg font-mono tabular-nums">{result.chart_forecast_count}</p>
                <p className="text-xs text-muted-foreground">Chart forecast</p>
              </div>
              <div className="border rounded p-2">
                <p className="text-lg font-mono tabular-nums">{result.chart_total_ids}</p>
                <p className="text-xs text-muted-foreground">Chart union</p>
              </div>
              <div className="border rounded p-2">
                <p className="text-lg font-mono tabular-nums">{result.drill_total_ids}</p>
                <p className="text-xs text-muted-foreground">Drill-down</p>
              </div>
            </div>

            {/* Set-difference: in chart but not drill-down */}
            {result.in_chart_not_drill?.count > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm font-medium">
                    In chart but NOT in drill-down ({result.in_chart_not_drill.count} records,{' '}
                    {formatCurrency(result.in_chart_not_drill.total, dc)})
                  </p>
                </div>
                <DivergenceTable items={result.in_chart_not_drill.items} dc={dc} />
              </div>
            )}

            {/* Set-difference: in drill-down but not chart */}
            {result.in_drill_not_chart?.count > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm font-medium">
                    In drill-down but NOT in chart ({result.in_drill_not_chart.count} records,{' '}
                    {formatCurrency(result.in_drill_not_chart.total, dc)})
                  </p>
                </div>
                <DivergenceTable items={result.in_drill_not_chart.items} dc={dc} />
              </div>
            )}

            {result.in_chart_not_drill?.count === 0 && result.in_drill_not_chart?.count === 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                No set-difference: both paths return the same bros_ids.
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              In both: {result.in_both_count} records
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DivergenceTable({ items, dc }) {
  if (!items?.length) return null;
  return (
    <div className="overflow-x-auto border rounded">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Booking</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Check-in</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="text-right">Amount ({dc})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, idx) => (
            <TableRow key={item.bros_id || idx}>
              <TableCell className="font-mono text-xs">
                <a
                  href={`/diagnostics?probe=${encodeURIComponent(item.bros_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                >
                  {item.bros_id || '(no id)'}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TableCell>
              <TableCell className="text-sm truncate max-w-[140px]" title={item.company}>
                {item.company}
              </TableCell>
              <TableCell className="text-xs">{(item.created_at || '').slice(0, 10) || 'N/A'}</TableCell>
              <TableCell className="text-xs">{(item.check_in || '').slice(0, 10) || 'N/A'}</TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">{item.status}</Badge>
              </TableCell>
              <TableCell>
                <Badge
                  variant={item.source === 'forecast' ? 'secondary' : 'outline'}
                  className="text-xs"
                >
                  {item.source}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {formatCurrency(item.amount, dc)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
